// Stylised, generic car diagram. Keep this DELIBERATELY simple: black
// outlines on a white card, no colors, no brand details. The whole point
// of the damage sheet is that staff can click any spot on the car and add
// a marker, not that the car looks pretty.
//
// All shapes live inside a 100×60 viewBox so markers can be stored as
// percentages (x_pct / y_pct) and the SVG can be resized freely.

import { useRef } from "react";

const STROKE = "#0f172a";   // slate-900
const STROKE_W = 0.5;
const FILL = "#ffffff";

function ViewFront() {
  return (
    <g fill={FILL} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round">
      {/* Outer body */}
      <path d="M20 50 Q 22 32 30 28 L70 28 Q 78 32 80 50 L80 53 L20 53 Z" />
      {/* Windshield */}
      <path d="M32 30 Q 36 22 50 21 Q 64 22 68 30 Z" />
      {/* Roof curve top */}
      <path d="M30 28 Q 50 18 70 28" />
      {/* Hood line */}
      <line x1="22" y1="45" x2="78" y2="45" />
      {/* Headlights */}
      <ellipse cx="28" cy="42" rx="4" ry="2" />
      <ellipse cx="72" cy="42" rx="4" ry="2" />
      {/* Grille */}
      <rect x="42" y="40" width="16" height="6" rx="1" />
      {/* Bumper line */}
      <line x1="18" y1="51" x2="82" y2="51" />
      {/* Wheels (poke through bumper) */}
      <circle cx="26" cy="53" r="3" fill={STROKE} />
      <circle cx="74" cy="53" r="3" fill={STROKE} />
    </g>
  );
}

function ViewBack() {
  return (
    <g fill={FILL} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round">
      {/* Body: slightly squarer than front */}
      <path d="M20 50 Q 22 32 28 28 L72 28 Q 78 32 80 50 L80 53 L20 53 Z" />
      {/* Rear window */}
      <path d="M30 30 L 70 30 L 66 22 L 34 22 Z" />
      <path d="M28 28 L 30 30 M 72 28 L 70 30" />
      {/* Tail lights */}
      <rect x="22" y="38" width="10" height="5" rx="1" />
      <rect x="68" y="38" width="10" height="5" rx="1" />
      {/* License plate */}
      <rect x="40" y="44" width="20" height="6" rx="1" />
      <line x1="18" y1="51" x2="82" y2="51" />
      <circle cx="26" cy="53" r="3" fill={STROKE} />
      <circle cx="74" cy="53" r="3" fill={STROKE} />
    </g>
  );
}

function ViewSide({ flip = false }) {
  // One side, generic 5-seater. `flip` reverses left/right for right-side view.
  return (
    <g
      fill={FILL}
      stroke={STROKE}
      strokeWidth={STROKE_W}
      strokeLinejoin="round"
      transform={flip ? "translate(100 0) scale(-1 1)" : undefined}
    >
      {/* Body shell */}
      <path d="M8 48 L 12 38 Q 22 28 40 26 L 62 26 Q 76 28 86 36 L 92 38 L 92 48 L 8 48 Z" />
      {/* Roof line */}
      <path d="M22 28 Q 40 16 62 22 L 70 26" />
      {/* Front door */}
      <path d="M32 28 L 32 46 M 32 28 L 38 22" />
      {/* Rear door */}
      <line x1="52" y1="28" x2="52" y2="46" />
      {/* Window divider */}
      <line x1="40" y1="22" x2="40" y2="28" />
      <line x1="60" y1="22" x2="60" y2="28" />
      {/* Door handles */}
      <line x1="36" y1="36" x2="42" y2="36" />
      <line x1="56" y1="36" x2="62" y2="36" />
      {/* Side mirrors */}
      <path d="M28 30 l -3 -2" />
      {/* Wheels */}
      <circle cx="24" cy="50" r="5" />
      <circle cx="76" cy="50" r="5" />
      <circle cx="24" cy="50" r="2" fill={STROKE} />
      <circle cx="76" cy="50" r="2" fill={STROKE} />
    </g>
  );
}

function ViewTop() {
  return (
    <g fill={FILL} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round">
      {/* Roof outline (top-down) */}
      <rect x="18" y="14" width="64" height="32" rx="6" />
      {/* Bonnet line */}
      <line x1="18" y1="22" x2="82" y2="22" />
      {/* Trunk line */}
      <line x1="18" y1="38" x2="82" y2="38" />
      {/* Roof panel */}
      <rect x="30" y="22" width="40" height="16" rx="2" />
      {/* Mirrors */}
      <rect x="14" y="24" width="4" height="4" />
      <rect x="82" y="24" width="4" height="4" />
      {/* Door split lines */}
      <line x1="30" y1="22" x2="30" y2="38" />
      <line x1="50" y1="22" x2="50" y2="38" />
      <line x1="70" y1="22" x2="70" y2="38" />
      {/* Wheels (peeking out) */}
      <rect x="22" y="13" width="6" height="4" rx="1" />
      <rect x="72" y="13" width="6" height="4" rx="1" />
      <rect x="22" y="43" width="6" height="4" rx="1" />
      <rect x="72" y="43" width="6" height="4" rx="1" />
    </g>
  );
}

function ViewInterior() {
  return (
    <g fill={FILL} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round">
      {/* Dashboard */}
      <path d="M14 20 L 86 20 L 80 30 L 20 30 Z" />
      {/* Steering wheel */}
      <circle cx="30" cy="24" r="4" />
      <circle cx="30" cy="24" r="1.4" fill={STROKE} />
      {/* Info screen */}
      <rect x="46" y="22" width="14" height="6" rx="0.5" />
      {/* Front seats */}
      <rect x="18" y="32" width="20" height="14" rx="2" />
      <rect x="62" y="32" width="20" height="14" rx="2" />
      {/* Center console */}
      <rect x="44" y="32" width="12" height="14" rx="1" />
      {/* Back seat row */}
      <rect x="16" y="48" width="68" height="6" rx="2" />
    </g>
  );
}

export function CarDiagram({
  view = "front",
  damages = [],
  selectedId = null,
  onSelect,
  onClickEmpty,
  readOnly = false,
}) {
  const svgRef = useRef(null);

  const handleClick = (e) => {
    if (readOnly || !onClickEmpty) return;
    // Don't fire if a marker swallowed the click already.
    if (e.target.closest("[data-marker]")) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x_pct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y_pct = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    onClickEmpty({ x_pct, y_pct, view });
  };

  const viewDamages = damages.filter((d) => d.view === view);

  return (
    <div className="relative w-full max-w-2xl mx-auto" style={{ aspectRatio: "5 / 3" }}>
      <svg
        ref={svgRef}
        viewBox="0 0 100 60"
        preserveAspectRatio="xMidYMid meet"
        className={`absolute inset-0 w-full h-full select-none ${readOnly ? "" : "cursor-crosshair"}`}
        onClick={handleClick}
        data-testid={`car-diagram-${view}`}
      >
        {view === "front"    && <ViewFront />}
        {view === "back"     && <ViewBack />}
        {view === "left"     && <ViewSide flip={false} />}
        {view === "right"    && <ViewSide flip={true} />}
        {view === "top"      && <ViewTop />}
        {view === "interior" && <ViewInterior />}
      </svg>

      {/* Damage markers: absolutely positioned so they stay clickable
          regardless of how the SVG is scaled. */}
      <div className="absolute inset-0 pointer-events-none">
        {viewDamages.map((d, idx) => {
          const isSelected = d.id === selectedId;
          const severity = d.severity || "small";
          const dot =
            severity === "big"    ? "bg-rose-600 ring-rose-300"
            : severity === "medium" ? "bg-orange-500 ring-orange-300"
            : "bg-amber-500 ring-amber-200";
          const repaired = d.status === "repaired";
          return (
            <button
              key={d.id || idx}
              type="button"
              data-marker
              data-testid={`damage-marker-${d.id || idx}`}
              title={`${d.tag_label || d.tag_code} · ${severity}`}
              onClick={(e) => { e.stopPropagation(); onSelect?.(d); }}
              style={{ left: `${d.x_pct}%`, top: `${d.y_pct}%` }}
              className={`pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full text-white text-[10px] font-bold grid place-items-center
                          ring-2 ${dot} ${repaired ? "opacity-40 line-through" : ""}
                          ${isSelected ? "ring-4 ring-emerald-400 scale-110" : ""}
                          shadow-md hover:scale-110 transition-transform`}
            >
              {idx + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}
